import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ChallengeDescription from './sections/ChallengeDescription/ChallengeDescription';
import GoalChallenge from './sections/ChallengeGoal/ChallengeGoal';
import ChallengeReview from './sections/ChallengeReview/ChallengeReview';
import SelectChallenge from './sections/SelectChallenge/SelectChallenge';
import SelectChallengeDates from './sections/SelectChallengeDates/SelectChallengeDates';
import BdsLinearProgressIndicator from '../../../shared/components/v3/BdsLinearProgressIndicator/BdsLinearProgressIndicator';
import BdsButton from '../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import SecondaryHero from '../../../shared/components/v3/Heros/SecondaryHero/SecondaryHero';
import Toast from '../../../shared/components/v3/Toast/Toast';
import { CHALLENGES_MAIN_ROUTE } from '../../../shared/constants/auth';
import {
  CHALLENGES_TOTAL_STEPS,
  CHALLENGE_DEFAULT_VALUES,
  CREATE_CHALLENGE_FINAL_STEP,
  HERO_DESCRIPTIONS
} from '../../../shared/constants/challenges';
import { ARROW_RIGHT_ICON } from '../../../shared/constants/images';
import {
  CLICK_ACTION,
  CREATE_CHALLENGE_CHOOSE_TYPE,
  CREATE_CHALLENGE_CONFIRM,
  CREATE_CHALLENGE_ERROR,
  CREATE_CHALLENGE_GOAL,
  CREATE_CHALLENGE_SELECT_DATES,
  CREATE_CHALLENGE_SUCCESS,
  CREATE_CHALLENGE_TITLE
} from '../../../shared/constants/tagManager';
import { CREATE_CHALLENGE_ERROR_TOAST } from '../../../shared/constants/toast';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { IChallenge, IChallengeInfo } from '../../../shared/interfaces/challenges';
import { Challenges } from '../../../shared/services/api/challenges/challenges';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_CHALLENGES } from '../../../shared/translations/challenges';
import { CreateChallengeSteps } from '../../../shared/types/types';
import { redirectToPage, toLocalStorage } from '../../../shared/utils/utils';

import './CreateChallenge.scss';

const CreateChallenge = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [challenge, setChallenge] = useState<IChallenge>(CHALLENGE_DEFAULT_VALUES);
  const [challengesInfo, setChallengesInfo] = useState<IChallengeInfo[]>([]);
  const [selectedChallengeInfo, setSelectedChallengeInfo] = useState<IChallengeInfo | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [stepReady, setStepReady] = useState(true);
  const [error, setError] = useState(false);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [returnToDate, setReturnToDate] = useState(false);
  const navigate = useNavigate();
  const textButton =
    currentStep === CREATE_CHALLENGE_FINAL_STEP
      ? TRANSLATION_CHALLENGES.createChallenge.createButton
      : TRANSLATION_CHALLENGES.createChallenge.nextButton;
  const progress = useMemo(() => {
    return Math.min(((currentStep + 1) / CHALLENGES_TOTAL_STEPS) * 100, 100);
  }, [currentStep]);

  const buttonDisabled = useMemo(() => {
    if (currentStep === 0) {
      return selectedChallengeInfo === undefined;
    } else {
      return loading || !stepReady;
    }
  }, [currentStep, selectedChallengeInfo, stepReady, loading]);

  const { company } = useContext(CompanyContext);
  const { t } = useTranslation();

  const getChallengeInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await Challenges.getChallengesInfo();
      setChallengesInfo(response);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectedCategory = useCallback(
    (challengeInfo: IChallengeInfo) => {
      const type = challengeInfo.type;
      setChallenge({ ...challenge, type });
      setSelectedChallengeInfo(challengeInfo);
    },
    [setChallenge, setSelectedChallengeInfo, challenge]
  );

  const handleNextStep = useCallback(() => {
    if (returnToDate) {
      setCurrentStep(CREATE_CHALLENGE_FINAL_STEP);
      setReturnToDate(false);
      return;
    }
    const newStep = Math.min(currentStep + 1, CREATE_CHALLENGE_FINAL_STEP);
    setCurrentStep(newStep);
    setStepReady(false);

    switch (newStep) {
      case 1:
        TagManagerService.pushEvent(CLICK_ACTION, CREATE_CHALLENGE_CHOOSE_TYPE, { type: selectedChallengeInfo?.type });
        break;
      case 2:
        TagManagerService.pushEvent(CLICK_ACTION, CREATE_CHALLENGE_SELECT_DATES, {
          start_date: challenge.startAt,
          end_date: challenge.endAt
        });
        break;
      case 3:
        TagManagerService.pushEvent(CLICK_ACTION, CREATE_CHALLENGE_TITLE, { title: challenge.name });
        break;
      case 4:
        TagManagerService.pushEvent(CLICK_ACTION, CREATE_CHALLENGE_GOAL, {
          goal: challenge.goal,
          type: selectedChallengeInfo?.goalMeasured
        });
        break;
    }
  }, [currentStep, selectedChallengeInfo, challenge, returnToDate]);

  const sendChallenge = useCallback(async () => {
    try {
      setLoadingPost(true);
      TagManagerService.pushEvent(CLICK_ACTION, CREATE_CHALLENGE_CONFIRM);
      await Challenges.postChallenge(company.id, challenge);
      TagManagerService.pushEvent(CLICK_ACTION, CREATE_CHALLENGE_SUCCESS);
      toLocalStorage('challengeCreated', 'true');
      navigate(CHALLENGES_MAIN_ROUTE);
    } catch {
      TagManagerService.pushEvent(CLICK_ACTION, CREATE_CHALLENGE_ERROR);
      setOpenToast(true);
    } finally {
      setLoadingPost(false);
    }
  }, [challenge, company.id, navigate]);

  const handlePreviousStep = useCallback(() => {
    if (returnToDate) {
      setCurrentStep(CREATE_CHALLENGE_FINAL_STEP + 1);
      setReturnToDate(false);
    }
    if (currentStep === 0) {
      redirectToPage(navigate, CHALLENGES_MAIN_ROUTE);
    } else {
      setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
    }
  }, [currentStep, navigate, returnToDate]);

  const updateChallenge = useCallback(
    (updatedFields: Partial<IChallenge>) => {
      setChallenge({ ...challenge, ...updatedFields });
    },
    [challenge]
  );

  const getSelectChallengeStep = useCallback(() => {
    return (
      <SelectChallenge
        selectedChallengeInfo={selectedChallengeInfo}
        challengesInfo={challengesInfo}
        loading={loading}
        error={error}
        setSelectedCategory={handleSelectedCategory}
        retry={getChallengeInfo}
      />
    );
  }, [challengesInfo, error, getChallengeInfo, handleSelectedCategory, loading, selectedChallengeInfo]);

  const getHeroDescription = useMemo(() => {
    return t(HERO_DESCRIPTIONS[currentStep as CreateChallengeSteps] || '');
  }, [currentStep, t]);

  const getSelectDateStep = useCallback(() => {
    return <SelectChallengeDates updateChallenge={updateChallenge} challenge={challenge} setStepReady={setStepReady} />;
  }, [updateChallenge, challenge]);

  const getGoalChallengeStep = useCallback(() => {
    return (
      <GoalChallenge
        challengeInfo={selectedChallengeInfo as IChallengeInfo}
        challenge={challenge}
        updateChallenge={updateChallenge}
        validateForm={setStepReady}
        key="goal-challenge-step"
      />
    );
  }, [selectedChallengeInfo, challenge, updateChallenge, setStepReady]);

  const getDescriptionChallengeStep = useCallback(() => {
    return (
      <ChallengeDescription
        updateChallenge={updateChallenge}
        challenge={challenge}
        circleIndicatorIconUrl={selectedChallengeInfo?.icon || ''}
        key={'challenge-description-step'}
        setStepReady={setStepReady}
      />
    );
  }, [updateChallenge, challenge, selectedChallengeInfo?.icon]);

  const getChallengeReview = useCallback(() => {
    if (!selectedChallengeInfo) return null;
    return (
      <ChallengeReview
        setStep={setCurrentStep}
        challegeInfo={selectedChallengeInfo}
        challenge={challenge}
        setReturnToDate={setReturnToDate}
        icon={selectedChallengeInfo.icon}
        betterfliesRewarded={100} // Hardcoded by now, may change
      />
    );
  }, [selectedChallengeInfo, challenge]);

  const renderStep = useMemo(() => {
    if (currentStep == CREATE_CHALLENGE_FINAL_STEP) setStepReady(true);
    const steps = [
      getSelectChallengeStep(),
      getSelectDateStep(),
      getDescriptionChallengeStep(),
      getGoalChallengeStep(),
      getChallengeReview()
    ];
    return steps[currentStep] || null;
  }, [
    currentStep,
    getSelectChallengeStep,
    getSelectDateStep,
    getDescriptionChallengeStep,
    getGoalChallengeStep,
    getChallengeReview
  ]);

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  useEffect(() => {
    getChallengeInfo();
  }, [getChallengeInfo]);

  return (
    <>
      <div className="create-challenge-container">
        <SecondaryHero
          title={t(TRANSLATION_CHALLENGES.hero.createChallenge.title)}
          description={getHeroDescription}
          color="accent-quaternary"
          bottomContent={<BdsLinearProgressIndicator variant="subtle" size="s" progress={progress} />}
          backAction={handlePreviousStep}
        />
        <div className="create-challenge-content">
          {renderStep}
          {!error && !loading && (
            <div className="create-challenge-button">
              <BdsButton
                variant="primary"
                size="l"
                action={currentStep == CREATE_CHALLENGE_FINAL_STEP ? sendChallenge : handleNextStep}
                iconRight={ARROW_RIGHT_ICON}
                disabled={buttonDisabled}
                loading={loadingPost}
              >
                {t(textButton)}
              </BdsButton>
            </div>
          )}
        </div>
      </div>
      <Toast
        title={t(CREATE_CHALLENGE_ERROR_TOAST.title)}
        description={t(CREATE_CHALLENGE_ERROR_TOAST.description)}
        icon={CREATE_CHALLENGE_ERROR_TOAST.icon}
        open={openToast}
        button={CREATE_CHALLENGE_ERROR_TOAST.button}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </>
  );
};

export default CreateChallenge;
